/**
 * One settings section's read, written once.
 *
 * Every section fetched its row the same way — an AbortController per
 * mount, the abort swallowed, the failure for a page-level banner, the
 * row held in state that saves then replace — and it was ten copies,
 * drifting in how the error was worded and whether a section could
 * re-read. This is the one copy.
 *
 * Contract:
 *   - `data` is null until the first read lands, and is what a section
 *     renders from; `setData` is how a save's response replaces it.
 *   - `loadError` is the read's failure, for the section's top banner;
 *     a later successful read clears it. An aborted read sets nothing.
 *   - `reload()` reads again, under the same rules, for a section whose
 *     save changes more than the row it wrote.
 *   - `skip` holds the read off and empties `data` (a section showing one
 *     of its pages instead of itself); clearing it reads again.
 *   - `load` is read through a ref, so callers may pass it inline.
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react";
import { errorMessage } from "./error-message";

export interface SettingsSource<T> {
  data: T | null;
  setData: Dispatch<SetStateAction<T | null>>;
  loadError: string | null;
  setLoadError: (next: string | null) => void;
  reload: () => Promise<void>;
}

export function useSettingsSource<T>(
  load: (signal?: AbortSignal) => Promise<T>,
  { skip = false }: { skip?: boolean } = {},
): SettingsSource<T> {
  const [data, setData] = useState<T | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const loadRef = useRef(load);
  loadRef.current = load;

  const read = useCallback(async (signal?: AbortSignal) => {
    try {
      const next = await loadRef.current(signal);
      if (signal?.aborted) return;
      setData(next);
      setLoadError(null);
    } catch (err) {
      if (signal?.aborted) return;
      if (err instanceof DOMException && err.name === "AbortError") return;
      setLoadError(errorMessage(err));
    }
  }, []);

  useEffect(() => {
    if (skip) {
      setData(null);
      return;
    }
    const ac = new AbortController();
    void read(ac.signal);
    return () => ac.abort();
  }, [skip, read]);

  const reload = useCallback(() => read(), [read]);

  return { data, setData, loadError, setLoadError, reload };
}
